import type { Feature, FeatureCollection, MultiPolygon, Polygon } from 'geojson'
import type { DistrictWithBoundaries } from '@/api/main/types.gen'

export type DistrictFeatureProperties = {
  districtId: number
  districtCode: string
  subdistrictCode: string
  label: string
  selected: boolean
  hovered: boolean
}

const isPositionArray = (value: unknown): value is number[][][] => Array.isArray(value)

const isPolygon = (value: unknown): value is Polygon => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const candidate = value as { type?: unknown; coordinates?: unknown }
  return candidate.type === 'Polygon' && isPositionArray(candidate.coordinates)
}

const isMultiPolygon = (value: unknown): value is MultiPolygon => {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  const candidate = value as { type?: unknown; coordinates?: unknown }
  return candidate.type === 'MultiPolygon' && Array.isArray(candidate.coordinates)
}

export const districtLabel = (district: { district_code: string; subdistrict_code: string }): string =>
  `${district.district_code}–${district.subdistrict_code}`

export const toggleDistrictId = (selected: number[], districtId: number): number[] =>
  selected.includes(districtId) ? selected.filter(id => id !== districtId) : [...selected, districtId]

export const splitSelectedFirst = <T>(items: T[], isSelected: (item: T) => boolean): { selected: T[]; rest: T[] } => {
  const selected: T[] = []
  const rest: T[] = []
  for (const item of items) {
    if (isSelected(item)) {
      selected.push(item)
    } else {
      rest.push(item)
    }
  }
  return { selected, rest }
}

export const retainVisibleDistrictIds = (selected: number[], visibleIds: Iterable<number>): number[] => {
  const visible = visibleIds instanceof Set ? visibleIds : new Set(visibleIds)
  const next = selected.filter(id => visible.has(id))
  return next.length === selected.length ? selected : next
}

const walkPositions = (geometry: Polygon | MultiPolygon, visit: (lng: number, lat: number) => void) => {
  if (geometry.type === 'Polygon') {
    geometry.coordinates.forEach(ring => ring.forEach(([lng, lat]) => visit(lng, lat)))
    return
  }
  geometry.coordinates.forEach(polygon => polygon.forEach(ring => ring.forEach(([lng, lat]) => visit(lng, lat))))
}

export const boundsFromDistrictCollection = (
  collection: FeatureCollection<Polygon | MultiPolygon, DistrictFeatureProperties>
): [[number, number], [number, number]] | null => {
  let minLng = Infinity
  let minLat = Infinity
  let maxLng = -Infinity
  let maxLat = -Infinity
  collection.features.forEach(feature => {
    walkPositions(feature.geometry, (lng, lat) => {
      if (lng < minLng) minLng = lng
      if (lat < minLat) minLat = lat
      if (lng > maxLng) maxLng = lng
      if (lat > maxLat) maxLat = lat
    })
  })
  if (!Number.isFinite(minLng)) {
    return null
  }
  return [
    [minLng, minLat],
    [maxLng, maxLat],
  ]
}

export const districtsToFeatureCollection = (
  districts: DistrictWithBoundaries[],
  selectedIds: number[],
  hoveredId: number | undefined
): FeatureCollection<Polygon | MultiPolygon, DistrictFeatureProperties> => ({
  type: 'FeatureCollection',
  features: districts.flatMap(district => {
    const geometry = district.boundaries_geojson
    if (!isPolygon(geometry) && !isMultiPolygon(geometry)) {
      return []
    }
    const feature: Feature<Polygon | MultiPolygon, DistrictFeatureProperties> = {
      type: 'Feature',
      id: district.id,
      properties: {
        districtId: district.id,
        districtCode: district.district_code,
        subdistrictCode: district.subdistrict_code,
        label: districtLabel(district),
        selected: selectedIds.includes(district.id),
        hovered: hoveredId === district.id,
      },
      geometry,
    }
    return [feature]
  }),
})
